import {
    Url,
    TranslationService,
    LanguageCode,
    GTMUtils,
    ApiService, AnalyticsEventName
} from "webshop-modules";
import { SearchResponse, ArtistListResponse, ErrorResponse } from "../../../../../webshop-api";
import { useAppliedFiltersStore } from "./AppliedFilters.store";
import { defineStore } from "pinia";
import { AxiosResponse } from 'axios';
import { AppliedFilters } from "../entitites/applied-filters.interface";
import { SearchResults } from "../entitites/search-results.interface";

export const useSearchResultsStore = defineStore("searchResults", {

    state: (): SearchResults => ({
        error: null,
        pageLength: 25,
        resultList: { results: [] },
        artistsList: { results: [] },
        filterList: {},
        quickSearchList: {},
        quickSearchArtistsList: {},
        haveMoreEvents: false,
        haveMoreArtists: false,
        inProgress: true,
        quickSearchInProgress: false,
        url: new Url(window.location.href),
        lang: TranslationService.getLanguageCode() !== LanguageCode.Slovenian
            ? TranslationService.getLanguageCode()
            : 'si',
        params: {}
    }),

    actions: {
        formatParams(content: AppliedFilters): void {
            this.$patch((state) => {
                if (content !== null) {
                    state.params = Object.entries(content).reduce(function (res: AppliedFilters, item) {
                        if (item[0] === 'searchText' && typeof item[1] === 'string') {
                            if (item[1].length > 0) {
                                res["searchText"] = item[1].replace(/\s/g, '+');
                            }
                        } else if (item[0] === 'lang') {
                            res["lang"] = item[1];
                        } else if (item[0] === 'dateStart' && item[1] !== undefined) {
                            const date = new Date(item[1] as Date);
                            res["dateStart"] = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().replace(/T.*Z/g, '');
                        } else if (item[0] === 'dateEnd' && item[1] !== undefined) {
                            const date = new Date(item[1] as Date);
                            res["dateEnd"] = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().replace(/T.*Z/g, '');
                        } else if (item[1] !== undefined && Array.isArray(item[1]) && item[1].length > 0) {
                            if (item[1].length > 0) {
                                res[item[0]] = item[1].toString();
                            }
                        }

                        return res;
                    }, {});
                } else {
                    state.params = {};
                }
            })
        },

        setError(content: string | null): void {
            this.$patch({ error: content });
        },
        
        setInProgress(content: boolean): void {
            this.$patch({ inProgress: content });
        },

        setQuickSearchInProgress(content: boolean): void {
            this.$patch({ quickSearchInProgress: content });
        },
        
        setHaveMoreEvents(content: boolean): void {
            this.$patch({ quickSearchInProgress: content });
            this.haveMoreEvents = content;
        },
        
        setHaveMoreArtists(content: boolean): void {
            this.$patch({ quickSearchInProgress: content });
            this.haveMoreArtists = content;
        },
        
        setResultList(content: SearchResponse): void {
            this.$patch({ resultList: content });
        },
        
        setQuickSearchList(content: SearchResponse): void {
            this.$patch({ quickSearchList: content });
        },
        
        setArtistsList(content: ArtistListResponse): void {
            this.$patch({ artistsList: content });
        },
        
        setQuickSearchArtistsList(content: ArtistListResponse): void {
            this.$patch({ quickSearchArtistsList: content });
        },

        applyEvents(payload: any) {
            const data = payload.response.data;

            if (data.totalCount !== undefined) {
                if (payload.isQuickSearch) {
                    this.setQuickSearchList(data);
                    this.setQuickSearchInProgress(false);
                } else {
                    this.setHaveMoreEvents(data.totalCount > this.pageLength);
                    this.setResultList(data);
                }
            }

            this.setInProgress(false);
        },

        applyArtists(payload: any) {
            const data = payload.response.data;
            if (data.totalCount !== undefined) {
                if (payload.isQuickSearch) {
                    this.setQuickSearchArtistsList(data);
                } else {
                    this.setHaveMoreArtists(data.totalCount > this.pageLength);
                    this.setArtistsList(data);
                    this.setInProgress(false);
                }
            }
        },

        eventsNextPage(api: ApiService) {
            if (this.inProgress) {
                return false;
            }

            if (!this.resultList.nextPage || !this.resultList.totalCount) {
                return false;
            }

            const params: AppliedFilters = this.params;
            const nextPageOffset = (new Url(this.resultList.nextPage).getSearchParam('offset')) ?? this.pageLength;
            params.offset = typeof nextPageOffset === "string"
                ? parseInt(nextPageOffset)
                : this.pageLength;

            if (this.resultList.totalCount < params.offset) {
                return false;
            }

            this.setInProgress(true);
            api.search(params).then((response) => {
                if (response.data.results && this.resultList.results) {
                    response.data.results = this.resultList.results.concat(response.data.results);

                    this.setResultList(response.data);
                }
            }).then(() => {
                const totalCount = this.resultList.totalCount ?? 0;
                const displayedResults = this.resultList.results?.length ?? 0;

                this.setHaveMoreEvents(totalCount > displayedResults);
                this.setInProgress(false);
            });
        },

        artistsNextPage(api: ApiService) {
            if (this.inProgress) {
                return false;
            }

            if (!this.artistsList.nextPage || !this.artistsList.totalCount) {
                return false;
            }

            const params: AppliedFilters = this.params;

            const nextPageOffset = (new Url(this.artistsList.nextPage).getSearchParam('offset')) ?? this.pageLength;
            params.offset = typeof nextPageOffset === "string"
                ? parseInt(nextPageOffset)
                : this.pageLength;

            if (this.artistsList.totalCount < params.offset) {
                return false;
            }

            this.setInProgress(true);

            api.searchArtists(params).then((response) => {
                if (response.data.results && this.artistsList.results) {
                    response.data.results = this.artistsList.results.concat(response.data.results);

                    this.setArtistsList(response.data);
                }
            }).then(() => {
                const totalCount = this.artistsList.totalCount ?? 0;
                const displayedResults = this.artistsList.results?.length ?? 0;

                this.setHaveMoreArtists(totalCount > displayedResults);
                this.setInProgress(false);
            });
        },

        async getData(isQuickSearch: boolean | { searchText: string }, api: ApiService) {
            const params: AppliedFilters = this.params as AppliedFilters;

            if (!isQuickSearch) {
                this.setInProgress(true);
                this.updateHistory(params);
            } else if (typeof isQuickSearch !== 'boolean') {
                if (this.quickSearchInProgress) {
                    return;
                }

                params.searchText = isQuickSearch.searchText;
                isQuickSearch = true;

                this.setQuickSearchInProgress(true);
            }

            GTMUtils.pushAnalyticsEventToDataLayer({
                event: AnalyticsEventName.SearchResults,
                search_term: this.params.searchText || '-',
            })

            try {
                const response = await api.search(params);

                if (response.status !== 200) {
                    this.setError(response.statusText);

                    return false;
                }

                this.applyEvents({ response, isQuickSearch });
                this.setInProgress(false);
            } catch (e) {
                const error = (<AxiosResponse>e).data as ErrorResponse;

                if (error !== undefined) {
                    this.setError(error.message);

                    return false;
                }

                this.setError('Something went wrong, unfortunately we cannot pinpoint the reason.');

                return false;
            }

            try {
                const response = await api.searchArtists(params);

                if (response.status !== 200) {
                    this.setError(response.statusText);

                    return false;
                }

                this.applyArtists({ response, isQuickSearch });
                this.setInProgress(false);
            } catch (e) {
                const error = (<AxiosResponse>e).data as ErrorResponse;

                if (error !== undefined) {
                    this.setError(error.message);

                    return false;
                }

                this.setError('Something went wrong, unfortunately we cannot pinpoint the reason.');
                return false;
            }
        },

        async getArtists(isQuickSearch: boolean | { searchText: string }, api: ApiService) {
            const params = useAppliedFiltersStore().$state as AppliedFilters;

            this.setInProgress(true);
            this.setArtistsList({});
            this.updateHistory(params);

            try {
                const response = await api.searchArtists(params);

                if (response.status !== 200) {
                    this.setError(response.statusText);

                    return false;
                }

                this.applyArtists({ response, isQuickSearch });
                this.setInProgress(false);
            } catch (e) {
                const error = (<AxiosResponse>e).data as ErrorResponse;

                if (error !== undefined) {
                    this.setError(error.message);

                    return false;
                }
                this.setError('Something went wrong, unfortunately we cannot pinpoint the reason.');

                return false;
            }
        },

        updateHistory(params: AppliedFilters) {
            const newUrl = Object.keys(params).length > 0
                ? `?${Object.entries(params).map(kv => kv.join('=')).join('&')}`
                : window.location.pathname;

            history.pushState({ ...params }, document.title, newUrl);
        },
    },

})
